import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    Crown,
    Dog,
    Check,
    Shield,
    Lock,
    Zap,
    ChartBar,
    Sparkles,
    ArrowRight,
    Info,
    CreditCard,
    Store,
    Clock,
    Heart,
    PawPrint,
    Star,
    BadgeCheck,
    Users,
    CheckCircle2,
    MessageCircle,
    Calendar,
    Award
} from 'lucide-react'
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import PricingButtonLanding from './PricingButtonLanding'

const PricingSection = () => {
    const [isAnnual, setIsAnnual] = useState(false);
    const [hoveredPlan, setHoveredPlan] = useState(null);
    const [showPromo, setShowPromo] = useState(true);

    const calculateAnnualPrice = (monthlyPrice) => {
        const annual = monthlyPrice * 12;
        const discount = annual * 0.2; // 20% discount
        return Math.floor(annual - discount);
    };

    const PLAN_CONFIGS = {
        Basic: {
            name: "Básico",
            price: isAnnual ? calculateAnnualPrice(49) : "49",
            monthlyPrice: "49",
            icon: PawPrint,
            description: "Para dueños de una mascota que quieren cuidar su salud de manera preventiva · Ideal para monitoreo mensual básico",
            features: [
                { text: "5 análisis al mes", highlight: true, icon: ChartBar },
                { text: "Evaluación detallada de salud visible", highlight: true, icon: CheckCircle2 },
                { text: "Guía de cuidados y alimentación", highlight: true, icon: Heart },
                { text: "Información completa de su raza", highlight: true, icon: Info },
                { text: "Acceso a veterinarios online", highlight: true, icon: MessageCircle },
                { text: "Historial de análisis", highlight: true, icon: Calendar }
            ],
            color: "text-blue-600",
            bgGradient: "from-blue-500 to-blue-600",
            saveText: "Menos de $2 al día por su bienestar",
            benefits: ["Ideal para cachorros", "Monitoreo mensual", "Soporte básico"]
        },
        Pro: {
            name: "Pro",
            price: isAnnual ? calculateAnnualPrice(99) : "99",
            monthlyPrice: "99",
            icon: Shield,
            description: "Para familias con 2-3 mascotas · Detecta problemas de salud antes de que sean costosos",
            features: [
                { text: "15 análisis al mes", highlight: true, icon: ChartBar },
                { text: "Evaluación detallada de salud visible", highlight: true, icon: CheckCircle2 },
                { text: "Guía de cuidados y alimentación premium", highlight: true, icon: Heart },
                { text: "Información completa de su raza + consejos", highlight: true, icon: Info },
                { text: "Atención veterinaria prioritaria 24/7", highlight: true, icon: MessageCircle },
                { text: "Historial de análisis + tendencias", highlight: true, icon: Calendar }
            ],
            color: "text-amber-600",
            bgGradient: "from-amber-500 to-orange-500",
            recommended: true,
            saveText: "$6.60 por mascota al mes · 67% de ahorro vs plan básico",
            benefits: ["Ideal para familias", "Atención prioritaria", "Análisis preventivos"]
        },
        Premium: {
            name: "Premium",
            price: isAnnual ? calculateAnnualPrice(149) : "149",
            monthlyPrice: "149",
            icon: Crown,
            description: "Para refugios, albergues, veterinarios o personas con más de 3 mascotas · Análisis ilimitados con atención 24/7",
            features: [
                { text: "Análisis ilimitados", highlight: true, icon: ChartBar },
                { text: "Evaluación detallada de salud visible", highlight: true, icon: CheckCircle2 },
                { text: "Guía de cuidados y alimentación VIP", highlight: true, icon: Heart },
                { text: "Información completa + asesoría personal", highlight: true, icon: Info },
                { text: "Atención veterinaria VIP 24/7", highlight: true, icon: MessageCircle },
                { text: "Historial completo + predicciones", highlight: true, icon: Calendar }
            ],
            color: "text-purple-600",
            bgGradient: "from-purple-500 to-purple-600",
            saveText: "Ideal para más de 3 mascotas · Ahorro ilimitado",
            benefits: ["Ideal para profesionales", "Atención VIP", "Sin límites"]
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5 }
        }
    };

    return (
        <section className="relative bg-gradient-to-br from-amber-50/50 via-orange-50/30 to-white py-16 sm:py-20 lg:py-24 overflow-hidden" id="pricing">
            {/* Enhanced Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 opacity-5" />
                <motion.div
                    className="absolute top-1/4 right-[10%] w-96 h-96 bg-amber-200/20 rounded-full blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.2, 0.3, 0.2]
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />
                <motion.div
                    className="absolute bottom-1/4 left-[10%] w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.4, 0.2]
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        repeatType: "reverse"
                    }}
                />

                {/* Floating Paw Prints */}
                <div className="absolute inset-0">
                    <PawPrint className="absolute h-32 w-32 -right-8 top-8 text-amber-900/5 rotate-12" />
                    <PawPrint className="absolute h-24 w-24 -left-6 bottom-6 text-amber-900/5 -rotate-12" />
                </div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative">
                {/* Enhanced Header */}
                <div className="text-center mb-12 sm:mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm 
                                px-4 py-2 rounded-full shadow-sm mb-4"
                    >
                        <Sparkles className="h-5 w-5 text-amber-600" />
                        <span className="text-base font-medium bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            Tecnología AI de Última Generación
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                    >
                        <span className="block text-amber-900">Elige el plan perfecto para</span>
                        <span className="bg-gradient-to-r from-amber-600 to-orange-600 text-transparent bg-clip-text">
                            el cuidado de tu mascota
                        </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="text-base sm:text-lg text-amber-700 max-w-3xl mx-auto mb-6"
                    >
                        Planes flexibles diseñados para cada tipo de familia y sus necesidades específicas.
                        Comienza hoy y dale a tu mascota el cuidado que merece.
                    </motion.p>

                  

                    {/* Trust Indicators */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-12"
                    >
                        {[
                            { icon: Users, text: "+10,000 mascotas felices" },
                            { icon: Shield, text: "Tecnología certificada" },
                            { icon: BadgeCheck, text: "Veterinarios verificados" }
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 
                                         rounded-lg shadow-sm border border-amber-100"
                            >
                                <item.icon className="h-5 w-5 text-amber-600" />
                                <span className="text-sm font-medium text-amber-900">{item.text}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* Pricing Cards */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto"
                >
                    {Object.entries(PLAN_CONFIGS).map(([planKey, planConfig]) => (
                        <motion.div
                            key={planKey}
                            variants={itemVariants}
                            onHoverStart={() => setHoveredPlan(planKey)}
                            onHoverEnd={() => setHoveredPlan(null)}
                            className={cn(
                                "relative flex flex-col h-full rounded-2xl p-6 sm:p-8 transition-all duration-300",
                                planConfig.recommended
                                    ? "bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 shadow-lg"
                                    : "bg-white border-2 border-gray-100 hover:border-amber-200 hover:shadow-lg",
                                hoveredPlan === planKey && "transform scale-[1.02]"
                            )}
                        >
                            {/* Popular Badge */}
                            {planConfig.recommended && (
                                <div className="absolute -top-4 left-6">
                                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-1.5 rounded-full shadow-md">
                                        <span className="text-sm font-medium text-white flex items-center gap-1">
                                            <Crown className="h-4 w-4" />
                                            Más Popular
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Plan Header */}
                            <div className="mb-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className={cn(
                                        "p-2.5 rounded-xl",
                                        planKey === "Basic" ? "bg-blue-50" :
                                            planKey === "Pro" ? "bg-amber-50" : "bg-purple-50"
                                    )}>
                                        <planConfig.icon className={cn(
                                            "h-6 w-6",
                                            planKey === "Basic" ? "text-blue-500" :
                                                planKey === "Pro" ? "text-amber-500" : "text-purple-500"
                                        )} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900">{planConfig.name}</h3>
                                </div>
                                <p className="text-gray-600 text-sm leading-relaxed">{planConfig.description}</p>
                            </div>

                            {/* Price */}
                            <div className="mb-6">
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-4xl font-bold text-gray-900">${planConfig.price}</span>
                                    <span className="text-gray-600">
                                        {isAnnual ? "/año" : "/mes"}
                                    </span>
                                </div>
                                {isAnnual && (
                                    <div className="text-sm text-green-600 flex items-center gap-1">
                                        <Check className="h-4 w-4" />
                                        <span>Ahorras ${calculateAnnualPrice(planConfig.monthlyPrice) - planConfig.price}</span>
                                    </div>
                                )}
                                <p className="text-xs text-amber-600 mt-2">{planConfig.saveText}</p>
                            </div>

                            {/* Features */}
                            <div className="space-y-4 mb-8 flex-1">
                                {planConfig.features.map((feature, idx) => (
                                    <div key={idx} className="flex items-start gap-3">
                                        <feature.icon className={cn(
                                            "h-5 w-5 flex-shrink-0",
                                            planKey === "Basic" ? "text-blue-500" :
                                                planKey === "Pro" ? "text-amber-500" : "text-purple-500"
                                        )} />
                                        <span className="text-gray-600 text-sm">{feature.text}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Benefits */}
                            <div className="bg-gray-50 rounded-xl p-4 mb-6">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Ideal para:</h4>
                                <ul className="space-y-2">
                                    {planConfig.benefits.map((benefit, idx) => (
                                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                            <PawPrint className="h-4 w-4 text-amber-500" />
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* CTA Button */}
                            <PricingButtonLanding
                                planConfig={planConfig}
                                className={cn(
                                    "w-full py-3 text-base rounded-xl transition-all duration-300",
                                    planConfig.recommended
                                        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg"
                                        : "bg-white border-2 border-gray-200 hover:border-amber-200 text-gray-900 hover:bg-amber-50"
                                )}
                            />
                        </motion.div>
                    ))}
                </motion.div>

                {/* Enhanced Footer */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="mt-16 text-center"
                >
                    {/* Payment Methods */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-amber-100 max-w-3xl mx-auto">
                        <h4 className="text-lg font-semibold text-amber-900 mb-4">Métodos de pago seguros</h4>
                        <div className="flex flex-wrap justify-center gap-4 mb-6">
                            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
                                <CreditCard className="h-5 w-5 text-gray-600" />
                                <span className="text-sm font-medium text-gray-900">Tarjetas</span>
                            </div>
                            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
                                <Store className="h-5 w-5 text-gray-600" />
                                <span className="text-sm font-medium text-gray-900">OXXO</span>
                            </div>
                            {/* Add more payment methods as needed */}
                        </div>

                        {/* Trust Elements */}
                        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                            <div className="flex items-center gap-1.5">
                                <Lock className="h-4 w-4 text-amber-600" />
                                <span>Pagos 100% seguros</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Shield className="h-4 w-4 text-amber-600" />
                                <span>SSL Certificado</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 className="h-4 w-4 text-amber-600" />
                                <span>Garantía de satisfacción</span>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Preview */}
                    <div className="mt-8 text-center">
                        <p className="text-amber-700">
                            ¿Tienes preguntas? Consulta nuestras{" "}
                            <button
                                onClick={() => document.getElementById('faq').scrollIntoView({ behavior: 'smooth' })}
                                className="text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2"
                            >
                                preguntas frecuentes
                            </button>
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default PricingSection;